// src/components/PatientInterests.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const API = process.env.REACT_APP_API_URL;

// ✅ Frontend-only suggestions (extend as you like)
const SUGGESTED_INTERESTS = [
  "Garten",
  "Reisen",
  "Natur",
  "Musik",
  "Kochen",
  "Sport",
  "Lesen",
  "Kunst",
  "Spaziergänge",
  "Haustiere",
];

export default function PatientInterests({
  patientId,
  initialInterests = [], // can be ["Garten","Reisen"] or [{id,name}]
  onSaved,               // (updatedInterestsStrings) => void
}) {
  const [editMode, setEditMode] = useState(false);

  // normalize initialInterests to [{id,name}]
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    const normalized = Array.isArray(initialInterests)
      ? initialInterests.map((it) =>
          typeof it === "string" ? { id: it, name: it } : it
        )
      : [];
    setSelected(normalized);
  }, [initialInterests]);

  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const boxRef = useRef(null);

  // filter suggestions locally
  const filteredSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sel = new Set(selected.map((s) => s.name.toLowerCase()));
    return SUGGESTED_INTERESTS
      .filter((name) => (q ? name.toLowerCase().includes(q) : true))
      .filter((name) => !sel.has(name.toLowerCase()))
      .slice(0, 20);
  }, [query, selected]);

  const selectedIds = useMemo(() => new Set(selected.map((i) => i.id)), [selected]);

  function removeInterest(id) {
    setSelected((prev) => prev.filter((i) => i.id !== id));
  }

  function addInterest(interest) {
    // interest can be string or {id,name}
    const obj = typeof interest === "string" ? { id: interest, name: interest } : interest;
    if (!obj?.name) return;
    if (selectedIds.has(obj.id)) return;
    setSelected((prev) => [...prev, obj]);
    setQuery("");
  }

  function onKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      const text = query.trim();
      if (!text) return;

      // If a suggested one matches exactly, add it; otherwise add as new custom interest
      const exact = SUGGESTED_INTERESTS.find(
        (s) => s.toLowerCase() === text.toLowerCase()
      );
      addInterest(exact || text);
    }
    if (e.key === "Escape") {
      setQuery("");
    }
  }

  async function handleSave() {
    setSaving(true);
    setErr("");
    try {
      // send list of strings to backend
      const interests = selected
        .map((i) => (typeof i === "string" ? i : i?.name))
        .map((s) => (s || "").trim())
        .filter(Boolean);

      const res = await fetch(`${API}/patients/interests`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: Number(patientId),
          interests, // ["Garten","Reisen", ...]
        }),
      });

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json(); // { patient_id, interests: ["...", "..."] }

      // normalize response to [{id,name}] for local UI
      const normalized = Array.isArray(data.interests)
        ? data.interests.map((it) =>
            typeof it === "string" ? { id: it, name: it } : it
          )
        : [];

      setSelected(normalized);
      setEditMode(false);
      onSaved?.(data.interests || []);
    } catch (e) {
      setErr(e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    // reset to props
    const normalized = Array.isArray(initialInterests)
      ? initialInterests.map((it) =>
          typeof it === "string" ? { id: it, name: it } : it
        )
      : [];
    setSelected(normalized);
    setQuery("");
    setErr("");
    setEditMode(false);
  }

  return (
    <section className="rounded-xl border border-black/10 bg-white/80 p-4" ref={boxRef}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900">Interessen</h2>
        </div>

        {editMode && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-[#76B3FF] px-3 py-1.5 text-white hover:bg-[#5d9ef7] disabled:opacity-60"
            >
              {saving ? "Speichert…" : "Speichern"}
            </button>
            <button
              onClick={handleCancel}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-gray-700 hover:bg-white/70"
            >
              Abbrechen
            </button>
          </div>
        )}
      </div>

      {err && (
        <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
          {err}
        </div>
      )}

      {/* Selected chips */}
      <div className="flex flex-wrap gap-2">
        {selected.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-2 rounded-full bg-blue-100/70 px-3 py-1 text-xs text-gray-800"
          >
            {tag.name}
            {editMode && (
              <button
                onClick={() => removeInterest(tag.id)}
                className="rounded-full px-1.5 text-gray-600 hover:bg-gray-200"
                title="Entfernen"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {selected.length === 0 && !editMode && (
          <span className="text-sm text-gray-600">—</span>
        )}
        {!editMode && (
          <button
            type="button"
            onClick={() => setEditMode(true)}
            aria-label="Interesse hinzufügen"
            title="Interesse hinzufügen"
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-blue-50"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5">
              <path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Input + local suggestions (no backend) */}
      {editMode && (
        <div className="mt-3 relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Interesse suchen oder eingeben und Enter…"
            className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-blue-400"
          />

          {/* Suggestion dropdown */}
          {query && (
            <div className="absolute z-10 mt-1 w-full rounded-md border border-black/10 bg-white shadow">
              {filteredSuggestions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-600">
                  Keine Vorschläge. Drücke Enter, um <b>{query}</b> hinzuzufügen.
                </div>
              ) : (
                filteredSuggestions.map((name) => (
                  <button
                    type="button"
                    key={name}
                    onClick={() => addInterest(name)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-blue-50"
                  >
                    <span>{name}</span>
                    {/* hide duplicate marker since we filter out selected ones */}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
